import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  User, 
  Bell, 
  Shield, 
  Eye, 
  HelpCircle, 
  FileText, 
  LogOut,
  ChevronRight,
  Moon,
  Globe,
  Trash2,
  Lock,
  MessageSquare,
  Heart,
  Users,
  Volume2,
  Vibrate,
  Mail,
  MapPin,
  BadgeCheck,
  Ticket,
  Crown,
  Gift,
  Check,
  Loader2,
  ExternalLink,
  Smartphone,
  Apple,
  Info
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import { api } from "@/lib/api";

// Move components outside of SettingsPage
const SettingItem = ({ icon: Icon, title, subtitle, onClick, rightElement, danger, badge }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors ${danger ? 'text-red-500' : ''}`}
  >
    <div className={`w-10 h-10 rounded-full flex items-center justify-center relative ${danger ? 'bg-red-50' : 'bg-gray-100'}`}>
      <Icon className={`w-5 h-5 ${danger ? 'text-red-500' : 'text-[#1C3A7A]'}`} />
      {badge > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </div>
    <div className="flex-1 text-left">
      <p className={`font-medium ${danger ? 'text-red-500' : 'text-gray-900'}`}>{title}</p>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
    {rightElement || <ChevronRight className="w-5 h-5 text-gray-400" />}
  </button>
);

const ToggleItem = ({ icon: Icon, title, subtitle, checked, onChange }) => (
  <div className="flex items-center gap-4 p-4">
    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#1C3A7A]" />
    </div>
    <div className="flex-1">
      <p className="font-medium text-gray-900">{title}</p>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
    <Switch 
      checked={checked} 
      onCheckedChange={onChange}
      className="data-[state=checked]:bg-[#FAB710]"
    />
  </div>
);

const SectionHeader = ({ title }) => (
  <div className="px-4 py-3 bg-[#f8f9fa]">
    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">{title}</h3>
  </div>
);

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  // Notification settings
  const [notifications, setNotifications] = useState({
    newMatches: true,
    messages: true,
    likes: true,
    superLikes: false,
    promotions: false,
    soundEnabled: true,
    vibration: true,
    emailNotifications: false,
  });

  // Privacy settings
  const [privacy, setPrivacy] = useState({
    showOnlineStatus: true,
    showDistance: true,
    showAge: true,
    readReceipts: true,
  });

  // App settings
  const [appSettings, setAppSettings] = useState({
    darkMode: false,
    language: "English",
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [unreadTicketCount, setUnreadTicketCount] = useState(0);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoResult, setPromoResult] = useState(null);
  const [showManageSubscription, setShowManageSubscription] = useState(false);
  
  // Detect platform
  const getPlatform = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return 'ios';
    }
    if (/android/i.test(userAgent)) {
      return 'android';
    }
    return 'web';
  };
  
  const platform = getPlatform();

  // Fetch unread ticket count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await api.getUnreadTicketCount();
        setUnreadTicketCount(response.data.unread_count || 0);
      } catch (error) {
        console.error("Failed to fetch unread count:", error);
      }
    };
    fetchUnreadCount();
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const handleDeleteAccount = () => {
    // In production, this would call an API endpoint
    toast.success("Account deletion request submitted");
    setShowDeleteConfirm(false);
    logout();
    navigate("/");
  };

  const toggleNotification = (key) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
    toast.success("Setting updated");
  };

  const togglePrivacy = (key) => {
    setPrivacy(prev => ({ ...prev, [key]: !prev[key] }));
    toast.success("Privacy setting updated");
  };

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }
    
    setPromoLoading(true);
    setPromoResult(null);
    
    try {
      const token = localStorage.getItem("token");
      const BACKEND_URL = "https://api.recoverymatch.com";
      const response = await fetch(`${BACKEND_URL}/api/promo-codes/redeem`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ code: promoCode.trim().toUpperCase() })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setPromoResult({ success: true, message: data.message });
        setPromoCode("");
        toast.success(data.message);
      } else {
        setPromoResult({ success: false, message: data.detail || "Invalid promo code" });
        toast.error(data.detail || "Invalid promo code");
      }
    } catch (error) {
      console.error("Promo code error:", error);
      setPromoResult({ success: false, message: "Failed to redeem code" });
      toast.error("Failed to redeem code");
    } finally {
      setPromoLoading(false);
    }
  };

  const handleManageSubscription = () => {
    if (platform === 'ios') {
      // Deep link to iOS subscription settings
      window.location.href = 'https://apps.apple.com/account/subscriptions';
    } else if (platform === 'android') {
      // Deep link to Google Play subscription settings
      window.location.href = 'https://play.google.com/store/account/subscriptions';
    } else {
      // Show instructions for web users
      setShowManageSubscription(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] pb-20">
      <div className="lg:max-w-3xl lg:mx-auto lg:px-6 lg:pt-8">
      {/* Themed Gradient Header */}
      <div 
        className="relative pt-12 pb-6 px-4 overflow-hidden lg:rounded-[25px]"
        style={{
          background: "linear-gradient(135deg, #1C3A7A 0%, #0d1f3c 50%, #061022 100%)",
          borderBottomLeftRadius: "25px",
          borderBottomRightRadius: "25px",
        }}
      >
        {/* Decorative Circles */}
        <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-white/[0.08]"></div>
        <div className="absolute bottom-3 -left-5 w-16 h-16 rounded-full bg-white/[0.05]"></div>

        {/* Top Bar */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="w-9"></div>
        </div>

        {/* User Profile Preview */}
        <button 
          onClick={() => navigate("/profile")}
          className="w-full flex items-center gap-4 bg-white/10 rounded-2xl p-4 hover:bg-white/15 transition-colors"
        >
          <div className="w-14 h-14 rounded-full overflow-hidden bg-gray-200 border-2 border-[#FAB710]">
            {user?.photos?.[0] ? (
              <img src={user.photos[0]} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-[#1C3A7A] flex items-center justify-center">
                <User className="w-7 h-7 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 text-left">
            <p className="font-semibold text-white">{user?.name || "User"}</p>
            <p className="text-sm text-white/70">{user?.email}</p>
          </div>
          <ChevronRight className="w-5 h-5 text-white/60" />
        </button>

        <h1 className="text-2xl font-bold text-white text-center mt-4 drop-shadow-sm">
          Settings
        </h1>
      </div>

      {/* Notifications Section */}
      <SectionHeader title="Notifications" />
      <div className="border-b border-gray-100">
        <ToggleItem
          icon={Heart}
          title="New Matches"
          subtitle="Get notified when you have a new match"
          checked={notifications.newMatches}
          onChange={() => toggleNotification('newMatches')}
        />
        <ToggleItem
          icon={MessageSquare}
          title="Messages"
          subtitle="Get notified for new messages"
          checked={notifications.messages}
          onChange={() => toggleNotification('messages')}
        />
        <ToggleItem
          icon={Users}
          title="Likes"
          subtitle="Get notified when someone likes you"
          checked={notifications.likes}
          onChange={() => toggleNotification('likes')}
        />
        <ToggleItem
          icon={Volume2}
          title="Sound"
          subtitle="Play sound for notifications"
          checked={notifications.soundEnabled}
          onChange={() => toggleNotification('soundEnabled')}
        />
        <ToggleItem
          icon={Vibrate}
          title="Vibration"
          subtitle="Vibrate for notifications"
          checked={notifications.vibration}
          onChange={() => toggleNotification('vibration')}
        />
        <ToggleItem
          icon={Mail}
          title="Email Notifications"
          subtitle="Receive notifications via email"
          checked={notifications.emailNotifications}
          onChange={() => toggleNotification('emailNotifications')}
        />
      </div>

      {/* Privacy Section */}
      <SectionHeader title="Privacy" />
      <div className="border-b border-gray-100">
        <ToggleItem
          icon={Eye}
          title="Show Online Status"
          subtitle="Let others see when you're online"
          checked={privacy.showOnlineStatus}
          onChange={() => togglePrivacy('showOnlineStatus')}
        />
        <ToggleItem
          icon={MapPin}
          title="Show Distance"
          subtitle="Show how far away you are"
          checked={privacy.showDistance}
          onChange={() => togglePrivacy('showDistance')}
        />
        <ToggleItem
          icon={Eye}
          title="Read Receipts"
          subtitle="Let others know when you've read their messages"
          checked={privacy.readReceipts}
          onChange={() => togglePrivacy('readReceipts')}
        />
      </div>

      {/* Account Section */}
      <SectionHeader title="Account" />
      <div className="border-b border-gray-100">
        <SettingItem
          icon={BadgeCheck}
          title="Get Verified"
          subtitle={user?.is_verified ? "You're verified!" : "Verify your profile"}
          onClick={() => navigate("/settings/verification")}
          rightElement={user?.is_verified ? (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">Verified</span>
          ) : null}
        />
        <SettingItem
          icon={Lock}
          title="Change Password"
          subtitle="Update your password"
          onClick={() => navigate("/reset-password")}
        />
        <SettingItem
          icon={Shield}
          title="Blocked Users"
          subtitle="Manage blocked accounts"
          onClick={() => navigate("/settings/blocked")}
        />
      </div>

      {/* Subscription & Promo Section */}
      <SectionHeader title="Subscription" />
      <div className="border-b border-gray-100">
        <div className="p-4">
          {/* Current Plan */}
          <div className="flex items-center gap-4 mb-4 p-3 bg-gradient-to-r from-[#1C3A7A]/5 to-[#FAB710]/5 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-[#FAB710]/20 flex items-center justify-center">
              <Crown className="w-5 h-5 text-[#FAB710]" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">
                {user?.subscription_tier === "recovery_elite" ? "Recovery Elite" :
                 user?.subscription_tier === "recovery_plus" ? "Recovery+" : "Free Plan"}
              </p>
              <p className="text-sm text-gray-500">
                {user?.subscription_tier && user?.subscription_tier !== "free" 
                  ? "Thank you for your support!" 
                  : "Upgrade for more features"}
              </p>
            </div>
            {(!user?.subscription_tier || user?.subscription_tier === "free") ? (
              <Button 
                size="sm" 
                className="bg-[#FAB710] hover:bg-[#e5a70f] text-[#1C3A7A]"
                onClick={() => toast.info("Upgrade coming soon!")}
              >
                Upgrade
              </Button>
            ) : (
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleManageSubscription}
                className="text-[#1C3A7A] border-[#1C3A7A]"
                data-testid="manage-subscription-btn"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Manage
              </Button>
            )}
          </div>
          
          {/* Promo Code Input */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Gift className="w-4 h-4 text-[#1C3A7A]" />
              <span className="text-sm font-medium text-gray-700">Have a promo code?</span>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Enter code (e.g., BETA2024)"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="flex-1 uppercase"
                data-testid="promo-code-input"
              />
              <Button
                onClick={handleRedeemPromo}
                disabled={promoLoading || !promoCode.trim()}
                className="bg-[#1C3A7A] hover:bg-[#15305f]"
                data-testid="redeem-promo-btn"
              >
                {promoLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Redeem"
                )}
              </Button>
            </div>
            
            {/* Promo Result */}
            {promoResult && (
              <div className={`flex items-center gap-2 p-3 rounded-lg ${
                promoResult.success 
                  ? "bg-green-50 text-green-700" 
                  : "bg-red-50 text-red-700"
              }`}>
                {promoResult.success ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Ticket className="w-4 h-4" />
                )}
                <span className="text-sm">{promoResult.message}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* App Settings Section */}
      <SectionHeader title="App Settings" />
      <div className="border-b border-gray-100">
        <ToggleItem
          icon={Moon}
          title="Dark Mode"
          subtitle="Switch to dark theme"
          checked={appSettings.darkMode}
          onChange={() => {
            setAppSettings(prev => ({ ...prev, darkMode: !prev.darkMode }));
            toast.info("Dark mode coming soon");
          }}
        />
        <SettingItem
          icon={Globe}
          title="Language"
          subtitle={appSettings.language}
          onClick={() => toast.info("More languages coming soon")}
        />
      </div>

      {/* Support Section */}
      <SectionHeader title="Support" />
      <div className="border-b border-gray-100">
        <SettingItem
          icon={HelpCircle}
          title="Support Tickets"
          subtitle={unreadTicketCount > 0 ? `${unreadTicketCount} new reply${unreadTicketCount > 1 ? 's' : ''}` : "Get help with RecoveryMatch"}
          onClick={() => navigate("/support")}
          badge={unreadTicketCount}
        />
        <SettingItem
          icon={Shield}
          title="Safety Tips"
          subtitle="Stay safe while dating"
          onClick={() => navigate("/safety")}
        />
        <SettingItem
          icon={MessageSquare}
          title="Contact Us"
          subtitle="Send us feedback or report issues"
          onClick={() => navigate("/contact")}
        />
        <SettingItem
          icon={FileText}
          title="Terms of Service"
          onClick={() => navigate("/terms")}
        />
        <SettingItem
          icon={Shield}
          title="Privacy Policy"
          onClick={() => navigate("/privacy")}
        />
      </div>

      {/* Danger Zone */}
      <SectionHeader title="Danger Zone" />
      <div className="border-b border-gray-100">
        <SettingItem
          icon={LogOut}
          title="Log Out"
          subtitle="Sign out of your account"
          onClick={handleLogout}
          danger
        />
        <SettingItem
          icon={Trash2}
          title="Delete Account"
          subtitle="Permanently delete your account"
          onClick={() => setShowDeleteConfirm(true)}
          danger
        />
      </div>

      {/* App Version */}
      <div className="py-8 text-center">
        <p className="text-sm text-gray-400">RecoveryMatch v1.0.0</p>
        <p className="text-xs text-gray-300 mt-1">Made with ❤️ for the recovery community</p>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Account?</h3>
              <p className="text-gray-500 text-sm">
                This action cannot be undone. All your data, matches, and messages will be permanently deleted within 30 days.
              </p>
              <p className="text-gray-400 text-xs mt-2">
                You can also request deletion at{" "}
                <a href="/delete-account" className="text-[#1C3A7A] underline">
                  recoverymatch.com/delete-account
                </a>
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 h-12 rounded-xl"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-12 bg-red-500 hover:bg-red-600 rounded-xl"
                onClick={handleDeleteAccount}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Subscription Modal (Web Users) */}
      {showManageSubscription && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#1C3A7A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-[#1C3A7A]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Manage Subscription</h3>
              <p className="text-gray-500 text-sm">
                Subscriptions are managed through your device's app store.
              </p>
            </div>
            
            {/* iOS Instructions */}
            <div className="mb-4 p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center">
                  <Apple className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900">iPhone / iPad</h4>
              </div>
              <ol className="text-sm text-gray-600 space-y-2 ml-2">
                <li>1. Open <strong>Settings</strong> app</li>
                <li>2. Tap your <strong>Apple ID</strong> at the top</li>
                <li>3. Tap <strong>Subscriptions</strong></li>
                <li>4. Find <strong>RecoveryMatch</strong></li>
                <li>5. Tap to manage or cancel</li>
              </ol>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-3"
                onClick={() => window.open('https://apps.apple.com/account/subscriptions', '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Apple Subscriptions
              </Button>
            </div>
            
            {/* Android Instructions */}
            <div className="mb-4 p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900">Android</h4>
              </div>
              <ol className="text-sm text-gray-600 space-y-2 ml-2">
                <li>1. Open <strong>Google Play Store</strong></li>
                <li>2. Tap your <strong>Profile icon</strong></li>
                <li>3. Tap <strong>Payments & subscriptions</strong></li>
                <li>4. Tap <strong>Subscriptions</strong></li>
                <li>5. Find <strong>RecoveryMatch</strong></li>
              </ol>
              <Button
                variant="outline"
                size="sm"
                className="w-full mt-3"
                onClick={() => window.open('https://play.google.com/store/account/subscriptions', '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open Google Play Subscriptions
              </Button>
            </div>
            
            {/* Help Note */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-700 mb-4">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <p>
                If you subscribed through our website, please contact support for assistance.
              </p>
            </div>
            
            <Button
              className="w-full h-12 bg-[#1C3A7A] hover:bg-[#15305f] rounded-xl"
              onClick={() => setShowManageSubscription(false)}
            >
              Close
            </Button>
          </div>
        </div>
      )}
      </div>

      <BottomNav />
    </div>
  );
};

export default SettingsPage;
